import { AppIcon } from '@/components/ui/app-icon';
import { GoogleCalendarIcon } from '@/containers/integrations/google-calendar-icon.component';
import { iconLinkedin } from '@/lib/icons';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

// Multicolour brand marks. The icon set has monochrome glyphs only, so the
// recognisable logos are inlined or served from /logos.

export function GmailMark({ className }: { className?: string }) {
  return (
    <svg viewBox='52 42 88 66' className={className} aria-hidden='true'>
      <path fill='#4285f4' d='M58 108h14V74L52 59v43c0 3.32 2.69 6 6 6' />
      <path fill='#34a853' d='M120 108h14c3.32 0 6-2.69 6-6V59l-20 15' />
      <path fill='#fbbc04' d='M120 48v26l20-15v-8c0-7.42-8.47-11.65-14.4-7.2' />
      <path fill='#ea4335' d='M72 74V48l24 18 24-18v26L96 92' />
      <path
        fill='#c5221f'
        d='M52 51v8l20 15V48l-5.6-4.2c-5.94-4.45-14.4-.22-14.4 7.2'
      />
    </svg>
  );
}

export function OutlookMark({ className }: { className?: string }) {
  return (
    <svg viewBox='0 0 48 48' className={className} aria-hidden='true'>
      <rect x='16' y='9' width='28' height='30' rx='3' fill='#28A8EA' />
      <path
        d='M16 20l14 9 14-9v16a3 3 0 0 1-3 3H19a3 3 0 0 1-3-3z'
        fill='#0078D4'
      />
      <rect x='4' y='13' width='22' height='22' rx='3' fill='#0F5FC0' />
      <ellipse
        cx='15'
        cy='24'
        rx='5'
        ry='6.2'
        fill='none'
        stroke='#fff'
        strokeWidth='2.6'
      />
    </svg>
  );
}

export function SlackMark({ className }: { className?: string }) {
  return (
    <svg viewBox='0 0 122.8 122.8' className={className} aria-hidden='true'>
      <path
        d='M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z'
        fill='#e01e5a'
      />
      <path
        d='M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z'
        fill='#36c5f0'
      />
      <path
        d='M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z'
        fill='#2eb67d'
      />
      <path
        d='M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z'
        fill='#ecb22e'
      />
    </svg>
  );
}

export function TeamsMark({ className }: { className?: string }) {
  return (
    <svg viewBox='0 0 48 48' className={className} aria-hidden='true'>
      <circle cx='36' cy='12' r='5' fill='#5059C9' />
      <path d='M32 21h11v12a6 6 0 0 1-11 3z' fill='#5059C9' />
      <circle cx='25' cy='10' r='7' fill='#7B83EB' />
      <path d='M16 20h19v15a10 10 0 0 1-19 0z' fill='#7B83EB' />
      <rect x='3' y='17' width='23' height='23' rx='2' fill='#6264A7' />
      <path d='M8 23h13v3h-5v11h-3V26H8z' fill='#FFFFFF' />
    </svg>
  );
}

const img = (src: string) =>
  function ImageMark({ className }: { className?: string }) {
    return <img src={src} alt='' className={cn('object-contain', className)} />;
  };

export type ConnectorId =
  | 'gmail'
  | 'outlook'
  | 'hubspot'
  | 'salesforce'
  | 'slack'
  | 'teams'
  | 'linkedin'
  | 'google_calendar'
  | 'notion'
  | 'fireflies'
  | 'fathom'
  | 'lemlist';

export interface ConnectorDef {
  id: ConnectorId;
  name: string;
  Mark: (props: { className?: string }) => ReactNode;
}

export const CONNECTORS: Record<ConnectorId, ConnectorDef> = {
  gmail: { id: 'gmail', name: 'Gmail', Mark: GmailMark },
  outlook: { id: 'outlook', name: 'Outlook', Mark: OutlookMark },
  hubspot: { id: 'hubspot', name: 'HubSpot', Mark: img('/logos/hubspot.svg') },
  salesforce: {
    id: 'salesforce',
    name: 'Salesforce',
    Mark: img('/logos/salesforce.svg'),
  },
  slack: { id: 'slack', name: 'Slack', Mark: SlackMark },
  teams: { id: 'teams', name: 'Microsoft Teams', Mark: TeamsMark },
  linkedin: {
    id: 'linkedin',
    name: 'LinkedIn',
    Mark: ({ className }) => (
      <AppIcon
        icon={iconLinkedin}
        className={cn('text-[#0A66C2]', className)}
        aria-hidden
      />
    ),
  },
  google_calendar: {
    id: 'google_calendar',
    name: 'Calendar',
    Mark: GoogleCalendarIcon,
  },
  notion: { id: 'notion', name: 'Notion', Mark: img('/logos/notion.svg') },
  fireflies: {
    id: 'fireflies',
    name: 'Fireflies',
    Mark: img('/logos/fireflies.svg'),
  },
  fathom: { id: 'fathom', name: 'Fathom', Mark: img('/logos/fathom.svg') },
  lemlist: { id: 'lemlist', name: 'lemlist', Mark: img('/logos/lemlist.svg') },
};
